import { useState } from "react";
import { useStartMessages } from "../../hooks/use-start-messages";
import { useGlobalClient } from "../../hooks/use-global-client";
import type { StartMessageFilterInput } from "../../types/start-message-types";

export function MessageTemplatesPage() {
  const [messageText, setMessageText] = useState("");
  const [templateName, setTemplateName] = useState("");
  const [templateType, setTemplateType] = useState<
    "SMS" | "CHAT" | "EMAIL" | "GENERAL"
  >("SMS");
  const [searchQuery, setSearchQuery] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<any>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const maxLength = 160;

  // Filter state for the hook
  const [filter, setFilter] = useState<StartMessageFilterInput>({});

  // Auth and client context
  const { selectedClient, isSuperAdmin } = useGlobalClient();

  // Use the proper hook with tenant context
  const {
    startMessages,
    loading,
    error,
    total,
    refetch,
    createStartMessage: createMessage,
    updateStartMessage: updateMessage,
    deleteStartMessage: deleteMessage,
  } = useStartMessages({
    filter,
    limit: 50,
  });

  // Calculate stats from the hook data
  const totalMessages = total || 0;
  const activeMessages = startMessages.filter(
    (msg: any) => msg.isActive
  ).length;
  const smsMessages = startMessages.length; // All start messages are SMS-like

  const handleCreateTemplate = async () => {
    if (!templateName.trim() || !messageText.trim()) {
      alert("Please fill in both Message Name and message content");
      return;
    }

    if (templateType === "SMS" && messageText.length > maxLength) {
      alert("SMS messages must be 160 characters or less");
      return;
    }

    setIsCreating(true);
    try {
      await createMessage({
        name: templateName.trim(),
        content: messageText.trim(),
        tags: [],
        isActive: true,
      });
      // Reset form state
      setMessageText("");
      setTemplateName("");
      setIsCreating(false);
      setEditingTemplate(null);
      setIsUpdating(false);
    } catch (error) {
      console.error("Failed to create message:", error);
      setIsCreating(false);
    }
  };

  const handleDeleteTemplate = async (id: string) => {
    if (window.confirm("Are you sure you want to delete this message?")) {
      try {
        await deleteMessage(id);
      } catch (error) {
        console.error("Failed to delete template:", error);
      }
    }
  };

  const handleToggleActive = async (message: any) => {
    try {
      await updateMessage(message.id, {
        id: message.id,
        isActive: !message.isActive,
      });
    } catch (error) {
      console.error("Failed to update template:", error);
    }
  };

  const handleEditTemplate = (message: any) => {
    setEditingTemplate(message);
    setTemplateName(message.name);
    setMessageText(message.content);
    setTemplateType("SMS"); // All start messages are SMS-like
  };

  const handleCancelEdit = () => {
    setEditingTemplate(null);
    setTemplateName("");
    setMessageText("");
    setTemplateType("SMS");
    setIsUpdating(false);
  };

  const handleUpdateTemplate = async () => {
    if (!templateName.trim() || !messageText.trim()) {
      alert("Please fill in both Message Name and message content");
      return;
    }

    if (templateType === "SMS" && messageText.length > maxLength) {
      alert("SMS messages must be 160 characters or less");
      return;
    }

    setIsUpdating(true);
    try {
      await updateMessage(editingTemplate.id, {
        id: editingTemplate.id,
        name: templateName.trim(),
        content: messageText.trim(),
        tags: editingTemplate.tags || [],
        isActive: editingTemplate.isActive,
      });
      // Reset all editing state
      setEditingTemplate(null);
      setTemplateName("");
      setMessageText("");
      setTemplateType("SMS");
      setIsUpdating(false);
    } catch (error) {
      console.error("Failed to update template:", error);
      setIsUpdating(false);
    }
  };

  // The hook already handles filtering, so we use startMessages directly
  const filteredMessages = startMessages;

  // Show loading state for super admins without client selection
  if (isSuperAdmin && !selectedClient) {
    return (
      <div className="space-y-6">
        <div className="border-b border-gray-200 pb-4">
          <h1 className="text-3xl font-bold text-gray-900">Start Messages</h1>
          <p className="mt-2 text-gray-600">
            Create and manage reusable start messages for SMS and chat
            communications.
          </p>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h2 className="text-lg font-semibold text-blue-900 mb-2">
            Select a Client
          </h2>
          <p className="text-blue-700">
            As a super admin, you can switch between different clients using the
            client selector in the top navigation.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="border-b border-gray-200 pb-4">
        <h1 className="text-3xl font-bold text-gray-900">Start Messages</h1>
        <p className="mt-2 text-gray-600">
          Create and manage reusable start messages for SMS and chat
          communications.
        </p>
        {isSuperAdmin && selectedClient && (
          <p className="mt-1 text-sm text-blue-600">
            Managing start messages for:{" "}
            <span className="font-medium">{selectedClient.clientName}</span>
          </p>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow border p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-blue-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                  />
                </svg>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">
                Total Messages
              </p>
              <p className="text-2xl font-semibold text-gray-900">
                {loading ? "..." : totalMessages}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow border p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">
                Active Messages
              </p>
              <p className="text-2xl font-semibold text-gray-900">
                {loading ? "..." : activeMessages}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow border p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-purple-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
                  />
                </svg>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">SMS Messages</p>
              <p className="text-2xl font-semibold text-gray-900">
                {loading ? "..." : smsMessages}
              </p>
            </div>
          </div>
        </div>

        {/* <div className="bg-white rounded-lg shadow border p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-orange-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 10V3L4 14h7v7l9-11h-7z"
                  />
                </svg>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Usage</p>
              <p className="text-2xl font-semibold text-gray-900">
                {loading ? "..." : totalUsage}
              </p>
            </div>
          </div>
        </div> */}
      </div>

      {/* Create/Edit Template */}
      <div className="bg-white rounded-lg shadow border">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">
              {editingTemplate
                ? "Edit Start Message"
                : "Create New Start Message"}
            </h2>
            {editingTemplate && (
              <button
                onClick={handleCancelEdit}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                title="Cancel Edit"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            )}
          </div>
        </div>
        <div className="p-6">
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label
                  htmlFor="template-name"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Message Name
                </label>
                <input
                  type="text"
                  id="template-name"
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., Welcome Message, Product Demo"
                />
              </div>

              {/* <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Type
                </label>
                <select
                  value={templateType}
                  onChange={(e) => setTemplateType(e.target.value as any)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="SMS">SMS (160 char limit)</option>
                  <option value="CHAT">Chat Message</option>
                  <option value="EMAIL">Email</option>
                  <option value="GENERAL">General</option>
                </select>
              </div> */}
            </div>

            <div>
              <label
                htmlFor="message-content"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Message Content
              </label>
              <div className="relative">
                <textarea
                  id="message-content"
                  value={messageText}
                  onChange={(e) => setMessageText(e.target.value)}
                  className={`w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none ${
                    templateType === "SMS" && messageText.length > maxLength
                      ? "border-red-300"
                      : "border-gray-300"
                  }`}
                  rows={4}
                  maxLength={templateType === "SMS" ? maxLength : undefined}
                  placeholder={`Enter your Start Message${
                    templateType === "SMS"
                      ? " (max 160 characters for SMS)"
                      : ""
                  }`}
                />
                {templateType === "SMS" && (
                  <div
                    className={`absolute bottom-2 right-2 text-xs ${
                      messageText.length > maxLength
                        ? "text-red-500"
                        : messageText.length > maxLength * 0.9
                        ? "text-orange-500"
                        : "text-gray-500"
                    }`}
                  >
                    {messageText.length}/{maxLength}
                  </div>
                )}
              </div>
              {templateType === "SMS" && messageText.length > maxLength && (
                <p className="mt-1 text-sm text-red-600">
                  Message exceeds SMS character limit. Consider shortening for
                  mobile users.
                </p>
              )}
            </div>

            <div className="flex justify-end space-x-3">
              {editingTemplate ? (
                <>
                  <button
                    type="button"
                    onClick={handleCancelEdit}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleUpdateTemplate}
                    disabled={isUpdating}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isUpdating ? "Updating..." : "Update Message"}
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      setMessageText("");
                      setTemplateName("");
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    Clear
                  </button>
                  <button
                    type="button"
                    onClick={handleCreateTemplate}
                    disabled={isCreating}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isCreating ? "Creating..." : "Create Message"}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Templates List */}
      <div className="bg-white rounded-lg shadow border">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">
              Start Messages
            </h2>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg
                  className="h-5 w-5 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  const value = e.target.value;
                  setSearchQuery(value);
                  setFilter({ search: value || undefined });
                }}
                placeholder="Search messages..."
                className="block w-64 pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              />
            </div>
          </div>
        </div>
        <div className="divide-y divide-gray-200">
          {loading ? (
            <div className="p-6 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-gray-500">Loading messages...</p>
            </div>
          ) : error ? (
            <div className="p-6 text-center">
              <p className="text-red-600">Error loading messages: {error}</p>
              <button
                onClick={() => refetch()}
                className="mt-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
              >
                Retry
              </button>
            </div>
          ) : filteredMessages.length === 0 ? (
            <div className="p-6 text-center">
              <p className="text-gray-500">
                {searchQuery
                  ? "No messages match your search."
                  : "No messages created yet."}
              </p>
            </div>
          ) : (
            filteredMessages.map((message: any) => (
              <div
                key={message.id}
                className="p-6 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-lg font-medium text-gray-900">
                        {message.name}
                      </h3>
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                        SMS
                      </span>
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full ${
                          message.isActive
                            ? "bg-green-100 text-green-800"
                            : "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {message.isActive ? "Active" : "Inactive"}
                      </span>
                    </div>
                    <p className="text-gray-600 mb-3">"{message.content}"</p>
                    <div className="flex items-center space-x-4 text-sm text-gray-500">
                      <span>Used {message.usageCount || 0} times</span>
                      {message.tags && message.tags.length > 0 && (
                        <div className="flex items-center space-x-1">
                          <span>Tags:</span>
                          {message.tags.map((tag: string, index: number) => (
                            <span
                              key={index}
                              className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                      <span>
                        Created{" "}
                        {new Date(message.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 ml-4">
                    <button
                      onClick={() => handleToggleActive(message)}
                      className={`p-2 rounded-lg transition-colors flex items-center space-x-2 ${
                        message.isActive
                          ? "text-gray-400 hover:text-orange-600 hover:bg-orange-50"
                          : "text-gray-400 hover:text-green-600 hover:bg-green-50"
                      }`}
                      title={message.isActive ? "Deactivate" : "Activate"}
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d={
                            message.isActive
                              ? "M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                              : "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                          }
                        />
                      </svg>
                      <span>Archive</span>
                    </button>
                    <button
                      onClick={() => handleEditTemplate(message)}
                      className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors flex items-center space-x-2"
                      title="Edit"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                        />
                      </svg>
                      <span> Edit </span>
                    </button>
                    <button
                      onClick={() => handleDeleteTemplate(message.id)}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors flex items-center space-x-2"
                      title="Delete"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                      <span>Delete</span>
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
